
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit, Trash2, Eye } from 'lucide-react';
import { API } from '../../utils/api';

export function AdminVendors({ headers }) {
  const [vendors, setVendors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  // V3.1: Removed cert_type field - all payments use App2App
  const [form, setForm] = useState({ vendor_name: '', swish_number: '', org_number: '', enabled: true });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API}/admin/vendors`, { headers });
      setVendors(res.data.vendors || []);
    } catch (e) {
      toast.error('Failed to fetch vendors');
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const filteredVendors = vendors.filter(v => 
    (v.vendor_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.swish_number || '').includes(searchTerm)
  );

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredVendors.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredVendors.map(v => v.vendor_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleExport = () => {
    // V3.1: Removed cert_type from export
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Vendor Name,Swish Number,Org Number,Enabled\n"
      + filteredVendors.map(v => `${v.vendor_name},${v.swish_number},${v.org_number},${v.enabled}`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "vendors.csv");
    document.body.appendChild(link);
    link.click();
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/vendors`, form, { headers });
      toast.success('Vendor created');
      setShowCreate(false);
      // V3.1: Removed cert_type field
      setForm({ vendor_name: '', swish_number: '', org_number: '', enabled: true });
      fetchVendors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create vendor');
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/admin/vendors/${id}`, data, { headers });
      toast.success('Vendor updated');
      fetchVendors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update vendor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await axios.delete(`${API}/admin/vendors/${id}`, { headers });
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Vendor Registry</h2>
        <div className="flex gap-2">
          <Input 
            placeholder="Search vendors..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button onClick={() => setShowCreate(true)}>+ Add Vendor</Button>
        </div>
      </div>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Vendor</CardTitle>
            <p className="text-sm text-muted-foreground">V3.1: All vendors use App2App payment flow</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Vendor Name</Label><Input value={form.vendor_name} onChange={e=>setForm({...form, vendor_name: e.target.value})} placeholder="e.g. My Store" /></div>
              <div><Label>Swish Number (Alias)</Label><Input value={form.swish_number} onChange={e=>setForm({...form, swish_number: e.target.value})} placeholder="e.g. 1234567890" /></div>
              <div><Label>Org Number</Label><Input value={form.org_number} onChange={e=>setForm({...form, org_number: e.target.value})} placeholder="e.g. 556677-8899" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={c=>setForm({...form, enabled: c})} />
              <Label>Enabled</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate}>Create Vendor</Button>
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3">
                <Checkbox
                  checked={filteredVendors.length > 0 && selectedIds.length === filteredVendors.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-vendors"
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Swish</th>
              <th className="p-3">Org</th>
              <th className="p-3">Sign Up</th>
              <th className="p-3">Last Seen</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.map(v => (
              <tr key={v.vendor_id} className="border-t">
                <td className="p-3">
                  <Checkbox checked={selectedIds.includes(v.vendor_id)} onCheckedChange={() => toggleSelect(v.vendor_id)} data-testid={`select-vendor-${v.vendor_id}`} />
                </td>
                <td className="p-3 font-medium">{v.vendor_name}</td>
                <td className="p-3">{v.swish_number || '—'}</td>
                <td className="p-3">{v.org_number || '—'}</td>
                <td className="p-3">{formatDate(v.created_at)}</td>
                <td className="p-3">{formatDate(v.last_login_at)}</td>
                <td className="p-3"><Badge variant={v.enabled ? 'default' : 'destructive'}>{v.enabled ? 'Active' : 'Disabled'}</Badge></td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <Button size="icon" variant="outline" data-testid={`edit-vendor-${v.vendor_id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" data-testid={`view-vendor-${v.vendor_id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" onClick={() => {
                      const newEnabled = !v.enabled;
                      handleUpdate(v.vendor_id, { enabled: newEnabled });
                    }} data-testid={`toggle-vendor-${v.vendor_id}`}>
                      {v.enabled ? '⛔' : '✅'}
                    </Button>
                    <Button size="icon" variant="destructive" onClick={() => handleDelete(v.vendor_id)} data-testid={`delete-vendor-${v.vendor_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredVendors.length === 0 && (
              <tr>
                <td className="p-4 text-center text-muted-foreground" colSpan={8}>No vendors found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
